import type { CourseColor } from "./colors"
import type { Permission } from "./permissions"

export interface Course {
  id: string
  type: "course"
  attributes: CourseAttributes
  meta: CourseMeta
  relationships: CourseRelationships
}

export interface CourseSummary {
  id: string
  type: "course-summary"
  attributes: { title: string; color: string }
  meta: CourseMeta
  relationships: {
    planbook?: { data: { type: "planbook"; id: string } }
  }
}

interface CourseRelationships {
  cardStackSummaries?: { data: Array<CardStackSummaryLinkage> }
  planbook?: { data: { type: "planbook"; id: string } }
  standardSets?: { data: Array<StandardSetLinkage> }
}

interface StandardSetLinkage {
  type: "standard-set"
  id: string
}

interface CardStackSummaryLinkage {
  type: "card-stack-summary"
  id: string
}

interface CourseAttributes {
  title: string
  color: CourseColor
  isNonInstructional: boolean
  courseDateTimeSetting: "UNSET" | "NO" | "YES_UNSET" | "YES_SAME" | "YES_DIFFERENT"
  version: number
  calendar: CourseCalendar
  standardSetIds: Array<string>
  /**
   * Opts this course out of filling its empty days from the template their rotation day carries —
   * they wait behind the Add Lesson button instead. Filling automatically is how the planner has
   * always worked, so this is the deviation: null is a course that never touched it, and reads the
   * same as false. Opting out withholds the template; it does not clear the assignment, so opting
   * back in restores the same one.
   */
  doNotApplyTemplatesAutomatically: boolean | null
  parentCourse: CourseParentInfo
  recentlyImportedCourseIds: Array<string>
  recentlyImportedCourses: Array<any>
  /**
   * The course that this one follows ("Follow a Class" in course settings). The followed course
   * may be in another planbook, and it might not be visible to all users who can view this
   * course (in which case the frontend won't show it to them).
   */
  followedCourse: FollowedCourse | null
  _revision: number
  _revisedBySessionId: string | null
}

export interface FollowedCourse {
  courseId: string
}

interface CourseMeta {
  permissions: Array<Permission>
}

export interface CourseCalendar {
  units: Array<Unit>
  dates: Array<CourseCalendarDate>
  datesOff: Array<string>
  datesForcedOn: Array<string>
  dateAnnotations: Array<CourseDateAnnotation>
  version: number
}

export interface CourseParentInfo {
  id: string | null
  version: number | null
  ownerIds: Array<string>
  planbookId: string | null
}

export interface Unit {
  id: string
  startDate: string
  startIndex: number
  endDate: string
  endIndex: number
  unitLength: number
  ancestorIds: Array<string>
  descendantIds: Array<string>
  dateStrings?: Array<string>
}

// This is used to create monomorphic objects that have the same key order
// https://www.builder.io/blog/monomorphic-javascript
export function createUnit(attrs: Partial<Unit>): Unit {
  const defaults = {
    id: "",
    startDate: "",
    startIndex: 0,
    endDate: "",
    endIndex: 0,
    unitLength: 0,
    ancestorIds: [],
    descendantIds: [],
    dateStrings: [],
  }
  return Object.assign({}, defaults, attrs)
}

export type CourseCalendarDate = CourseCalendarDateCustom | CourseCalendarDateDefault

export interface CourseCalendarDateCustom {
  id: string
  type: "course-date-custom"
  attributes: CourseCalendarDateCustomAttributes
}

export interface CourseCalendarDateDefault {
  id: string
  type: "course-date-default"
  attributes: CourseCalendarDateDefaultAttributes
}

export interface CourseCalendarDateCustomAttributes {
  dateString: string
  cardStackId: string | null
  time: CourseCalendarTime | null
  isForcedOn: boolean
  unitStart: Array<string>
  unitEnd: Array<string>
  unitIds: Array<string>
}

// This is used to create monomorphic objects that have the same key order
// https://www.builder.io/blog/monomorphic-javascript
interface CourseDateCustomRequiredOptions extends Partial<CourseCalendarDateCustomAttributes> {
  dateString: string
}

export function createCourseDateCustomAttributes(
  attrs: CourseDateCustomRequiredOptions,
): CourseCalendarDateCustomAttributes {
  const defaults = {
    dateString: "",
    cardStackId: null,
    time: null,
    isForcedOn: false,
    unitStart: [],
    unitEnd: [],
    unitIds: [],
  }
  return Object.assign({}, defaults, attrs)
}

export interface CourseCalendarDateDefaultAttributes {
  rotationId: string
  semesterId: string
  cardStackId: string | null
  time: CourseCalendarTime | null
}

export interface CourseCalendarTime {
  /** In minutes since midnight. */
  start: number
  /** In minutes since midnight. */
  end: number
}

// This is used to create monomorphic objects that have the same key order
// https://www.builder.io/blog/monomorphic-javascript

interface CourseDateDefaultRequiredOptions extends Partial<CourseCalendarDateDefaultAttributes> {
  semesterId: string
  rotationId: string
}

export function createCourseDateDefaultAttributes(
  attrs: CourseDateDefaultRequiredOptions,
): CourseCalendarDateDefaultAttributes {
  const defaults = {
    semesterId: "",
    rotationId: "",
    cardStackId: null,
    time: null,
  }
  return Object.assign({}, defaults, attrs)
}

export type CourseDateAnnotation = CourseDateAnnotationSingle | CourseDateAnnotationDefault

export interface CourseDateAnnotationSingle {
  id: string
  type: "date-annotation-course-custom-single"
  attributes: {
    /** Null on notes saved without one — the no-class flow writes null, not "". */
    title: string | null
    description: string | null
    isOff: boolean
    isPublic: boolean
    dateString: string
    showAnnotation: boolean
  }
}

export interface CourseDateAnnotationDefault {
  id: string
  type: "date-annotation-course-default"
  attributes: {
    semesterId: string
    rotationId: string
    isOff: boolean
  }
}
